import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Heart, Shield, MessageCircle, AlertTriangle } from "lucide-react";

/**
 * AI Disclaimer & Onboarding Component
 * Empathetic, supportive message for hospice AI assistant
 * Clearly states it's supplemental, not a substitute for medical advice
 */
export default function AIDisclaimer({ onAccept, show = false }) {
  const [accepted, setAccepted] = useState(false);
  const [open, setOpen] = useState(show);

  useEffect(() => {
    // Check if user has already accepted
    const hasAccepted = localStorage.getItem("ai_disclaimer_accepted");
    if (!hasAccepted && show) {
      setOpen(true);
    }
  }, [show]);

  const handleAccept = () => {
    localStorage.setItem("ai_disclaimer_accepted", "true");
    setOpen(false);
    if (onAccept) onAccept();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg bg-gradient-to-b from-white to-[#F0F7F4]">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-[#E8F4F0] flex items-center justify-center">
              <Heart className="w-8 h-8 text-[#4A7C6F]" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl text-slate-800">
            Welcome to Your Compassionate AI Guide
          </DialogTitle>
          <DialogDescription className="text-center text-slate-600 mt-2">
            Goodwin Hospice AI Assistant
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Empathetic Onboarding Message */}
          <div className="bg-white rounded-xl p-5 border border-[#6B9B8A]/30 shadow-sm">
            <p className="text-slate-700 leading-relaxed text-center">
              <strong className="text-[#4A7C6F]">We're here to support you</strong> through 
              this journey with understanding and care. Our AI assistant can help answer 
              your questions about hospice care, guide you through symptom management, 
              and connect you with resources — whenever you need them, day or night.
            </p>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800 mb-1">Important Information</h4>
                <p className="text-sm text-amber-700 leading-relaxed">
                  This AI assistant is a <strong>supplemental tool for education and support</strong>. 
                  It is <strong>not a substitute</strong> for professional medical advice, 
                  diagnosis, or emergency services. For urgent medical needs, please contact 
                  your hospice team directly or call 911.
                </p>
              </div>
            </div>
          </div>

          {/* What the AI Can Help With */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
              <MessageCircle className="w-6 h-6 mx-auto text-[#4A7C6F] mb-2" />
              <p className="text-xs text-slate-600 font-medium">Answer Questions</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
              <Shield className="w-6 h-6 mx-auto text-[#4A7C6F] mb-2" />
              <p className="text-xs text-slate-600 font-medium">Guide Symptom Care</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
              <Heart className="w-6 h-6 mx-auto text-[#4A7C6F] mb-2" />
              <p className="text-xs text-slate-600 font-medium">Provide Comfort</p>
            </div>
          </div>

          {/* Acceptance Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
            <Checkbox 
              id="accept" 
              checked={accepted} 
              onCheckedChange={setAccepted}
              className="mt-0.5"
            />
            <Label htmlFor="accept" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
              I understand that this AI assistant provides educational support and is not 
              a replacement for professional medical care. I will contact my care team 
              or emergency services for urgent medical needs.
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={handleAccept}
            disabled={!accepted}
            className="w-full bg-[#4A7C6F] hover:bg-[#3D6B5F] text-white py-6 text-lg rounded-xl"
          >
            <Heart className="w-5 h-5 mr-2" />
            I Understand — Let's Begin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline AI Disclaimer Banner
 * For displaying at the top of AI chat interfaces
 */
export function AIDisclaimerBanner() {
  return (
    <div 
      className="bg-gradient-to-r from-[#E8F4F0] to-[#F0F7F4] border-b border-[#6B9B8A]/30 px-4 py-3"
      data-testid="ai-disclaimer-banner"
    >
      <div className="max-w-3xl mx-auto flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#4A7C6F]/10 flex items-center justify-center flex-shrink-0">
          <Heart className="w-4 h-4 text-[#4A7C6F]" />
        </div>
        <p className="text-sm text-[#4A7C6F]">
          <strong>Your compassionate AI guide</strong> — Here to support your journey 
          with information and care. For medical emergencies, always contact your 
          hospice team or call 911.
        </p>
      </div>
    </div>
  );
}
